import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { portalCandidateService, UserRegistrationData, PortalCandidateRegistrationData } from '../api/portalCandidateService';
import { userService } from '../api/userService';

interface PlanterProfileSummary {
  id: number;
  mbcbReportingEnabled?: boolean;
  [key: string]: unknown;
}

interface User {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  username?: string;
  isEmailVerified: boolean;
  roles?: string[];
  token?: string;
  refreshToken?: string;
  /** Present when this user is linked to a `church_planters` row (ChurchPlanter / planterProfile). */
  planterProfile?: PlanterProfileSummary | null;
}

interface PortalCandidate {
  id: number;
  userId: number;
  name: string;
  portalStatus: string;
  onboardingStep: number;
  onboardingCompleted: boolean;
  [key: string]: any;
}

interface AuthContextType {
  user: User | null;
  portalCandidate: PortalCandidate | null;
  /** Logged-in user linked to a church planter record but no `portal_candidates` row (e.g. planter app invite). They should not see portal onboarding UI. */
  isInvitedPlanterOnly: boolean;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<User>;
  register: (userData: UserRegistrationData) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  updateCandidate: (data: any) => Promise<void>;
  updateUser: (data: Partial<User>) => Promise<User>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [portalCandidate, setPortalCandidate] = useState<PortalCandidate | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const isAuthenticated = !!user;

  const isInvitedPlanterOnly = Boolean(user?.planterProfile) && !portalCandidate;

  // Initialize auth state from localStorage
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const token = localStorage.getItem('token');
        const savedUser = localStorage.getItem('user');
        
        if (token && savedUser) {
          setUser(JSON.parse(savedUser));
          await refreshUser();
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
        // Clear invalid data
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  const loadPortalCandidate = async () => {
    try {
      const response = await portalCandidateService.getCurrentCandidate();
      setPortalCandidate(response.portalCandidate || response.candidate);
    } catch (error) {
      console.error('Error loading portal candidate:', error);
      // User might not have a portal candidate profile yet
    }
  };

  const login = async (email: string, password: string) => {
    try {
      const response = await portalCandidateService.login({ email, password });
      
      // Store token and user data (token is inside the user object)
      localStorage.setItem('token', response.user.token);
      localStorage.setItem('user', JSON.stringify(response.user));
      
      setUser(response.user);
      await refreshUser();
      
      // Return user data for role-based redirect logic
      return response.user;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const register = async (userData: UserRegistrationData) => {
    try {
      setIsLoading(true);
      
      // Transform user registration data to portal candidate registration
      const registrationData: PortalCandidateRegistrationData = {
        ...userData,
        candidateData: {
          name: `${userData.firstName} ${userData.lastName}`,
          contactInfo: {
            email: userData.email,
          },
        },
      };
      
      const response = await portalCandidateService.register(registrationData);
      
      // Store token and user data (token is inside the user object)
      localStorage.setItem('token', response.user.token);
      localStorage.setItem('user', JSON.stringify(response.user));
      
      setUser(response.user);
      setPortalCandidate(response.portalCandidate);
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      await portalCandidateService.logout();
    } catch (error) {
      // Ignore errors during logout - we'll clear local state anyway
      console.log('Logout error (ignored):', error);
    }
    // Always clear local state regardless of API call success
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setPortalCandidate(null);
  };

  const refreshUser = async () => {
    try {
      const existingUser = user || (localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user') as string) : null);
      const response = await userService.getMe();
      const refreshedUser = response.user || response.data || response;
      if (refreshedUser) {
        const mergedUser = {
          ...existingUser,
          ...refreshedUser,
          token: existingUser?.token,
          refreshToken: existingUser?.refreshToken,
        };
        setUser(mergedUser as User);
        localStorage.setItem('user', JSON.stringify(mergedUser));
      }
      await loadPortalCandidate();
    } catch (error) {
      console.error('Error refreshing user:', error);
      throw error;
    }
  };

  const updateCandidate = async (data: any) => {
    try {
      const response = await portalCandidateService.updateCandidate(data);
      setPortalCandidate(response.portalCandidate || response.candidate);
      return response;
    } catch (error) {
      console.error('Error updating candidate:', error);
      throw error;
    }
  };

  const updateUser = async (data: Partial<User>) => {
    if (!user) {
      throw new Error('No user loaded');
    }
    try {
      const response = await userService.updateUser(user.id, data);
      const updatedUser = response.data || response.user || response;
      const mergedUser = {
        ...user,
        ...updatedUser,
        token: user.token,
        refreshToken: user.refreshToken,
      };
      setUser(mergedUser);
      localStorage.setItem('user', JSON.stringify(mergedUser));
      return mergedUser;
    } catch (error) {
      console.error('Error updating user:', error);
      throw error;
    }
  };

  const value: AuthContextType = {
    user,
    portalCandidate,
    isInvitedPlanterOnly,
    isLoading,
    isAuthenticated,
    login,
    register,
    logout,
    refreshUser,
    updateCandidate,
    updateUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
